import { reactive, readonly, ref, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave } from 'vue-router/composables'
import store from '@/store'
import { useWindowUnload } from '@/use/useWindowUnload'

/**
 * 未儲存提示對話框的文案設定。
 *
 * @typedef {Object} UnsavedLeaveDialogOptions
 * @property {string} [title] - 對話框標題。
 * @property {string} [content] - 對話框內容。
 * @property {string} [confirmText] - 儲存按鈕文字。
 * @property {string} [discardText] - 不儲存按鈕文字。
 * @property {string} [unloadText] - 瀏覽器 unload 提示文字。
 */

/**
 * 註冊未儲存離開保護時所需的回調設定。
 *
 * @typedef {Object} UnsavedLeavePromptOptions
 * @property {Function|Object} [isDirty] - dirty 判斷函式，或回傳 boolean 的 ref。
 * @property {Function} [onSave] - 選擇儲存後要執行的非同步邏輯；回傳 false 代表儲存失敗且中止續行。
 * @property {Function} [onDiscard] - 選擇不儲存後要執行的清理邏輯。
 * @property {UnsavedLeaveDialogOptions} [dialogOptions] - 自訂提示文案。
 */

const defaultDialogOptions = {
  title: '提醒',
  content: '您有尚未儲存的內容，是否確定要離開？',
  confirmText: '儲存',
  discardText: '不儲存',
  unloadText: '您有尚未儲存的內容，離開後將不會保留目前變更。',
}

const dialogState = reactive({
  visible: false,
  saving: false,
  title: defaultDialogOptions.title,
  content: defaultDialogOptions.content,
  confirmText: defaultDialogOptions.confirmText,
  discardText: defaultDialogOptions.discardText,
})

const activeGuard = ref(null)
const pendingAction = ref(null)

const resetDialogState = () => {
  dialogState.visible = false
  dialogState.saving = false
  dialogState.title = defaultDialogOptions.title
  dialogState.content = defaultDialogOptions.content
  dialogState.confirmText = defaultDialogOptions.confirmText
  dialogState.discardText = defaultDialogOptions.discardText
}

const clearPendingAction = () => {
  pendingAction.value = null
}

const isCurrentGuard = (guardId) => {
  return Boolean(activeGuard.value && activeGuard.value.id === guardId)
}

const runPendingProceed = async () => {
  const action = pendingAction.value
  clearPendingAction()
  resetDialogState()
  if (action && action.proceed) {
    await action.proceed()
  }
}

const runPendingCancel = async () => {
  const action = pendingAction.value
  clearPendingAction()
  resetDialogState()
  if (action && action.cancel) {
    await action.cancel()
  }
}

const normalizeDialogOptions = (dialogOptions = {}) => {
  return {
    ...defaultDialogOptions,
    ...dialogOptions,
  }
}

const openPrompt = ({ guardId, proceed, cancel }) => {
  if (!activeGuard.value || activeGuard.value.id !== guardId) {
    return
  }

  dialogState.title = activeGuard.value.dialogOptions.title
  dialogState.content = activeGuard.value.dialogOptions.content
  dialogState.confirmText = activeGuard.value.dialogOptions.confirmText
  dialogState.discardText = activeGuard.value.dialogOptions.discardText
  dialogState.visible = true
  pendingAction.value = {
    guardId,
    proceed,
    cancel,
  }
}

/**
 * 註冊目前頁面的未儲存離開保護。
 *
 * 這個 composable 會統一處理三種情境：
 * 1. router 離開目前頁面
 * 2. 頁面內需要保護的自訂動作
 * 3. 瀏覽器重新整理或關閉分頁
 *
 * 頁面只需要提供 dirty 判斷、儲存與放棄邏輯；實際的提示顯示與續行時機由全局 host 接手。
 *
 * 注意：此 composable 目前為頁面級單一 guard 設計。
 * 同一時間只支援一個元件註冊；若在同一頁面多處並行使用，後註冊者會覆蓋先註冊者。
 *
 * @param {UnsavedLeavePromptOptions} [options={}] - 未儲存保護設定。
 * @returns 回傳可包裝頁內受保護動作的 helper。
 *
 * @example
 * const { runGuarded } = useUnsavedLeavePrompt({
 *   isDirty: () => formRef.value?.isDirty,
 *   onSave: () => formRef.value?.save(),
 *   onDiscard: () => formRef.value?.reset(),
 *   dialogOptions: {
 *     content: '您有尚未儲存的設定，是否確定要離開？',
 *   },
 * })
 *
 * runGuarded(() => {
 *   showCreateDialog.value = true
 * })
 */
export const useUnsavedLeavePrompt = ({ isDirty, onSave, onDiscard, dialogOptions } = {}) => {
  const { registerWarnUnloadEvent } = useWindowUnload()
  const guardId = Symbol('unsaved-leave-guard')

  if (process.env.NODE_ENV !== 'production' && activeGuard.value) {
    console.warn(
      '[useUnsavedLeavePrompt] A guard is already active. This composable currently supports only one active guard at a time.',
      'The existing guard will be replaced by the latest registration.',
    )
  }

  const evaluateDirty = () => {
    if (typeof isDirty === 'function') {
      return Boolean(isDirty())
    }

    return Boolean(isDirty && isDirty.value)
  }

  const discardChanges = async () => {
    if (typeof onDiscard === 'function') {
      await onDiscard()
    }
  }

  const saveChanges = async () => {
    if (typeof onSave !== 'function') {
      return true
    }

    const result = await onSave()
    return result !== false
  }

  activeGuard.value = {
    id: guardId,
    dialogOptions: normalizeDialogOptions(dialogOptions),
    evaluateDirty,
    discardChanges,
    saveChanges,
  }

  registerWarnUnloadEvent({
    text: activeGuard.value.dialogOptions.unloadText,
    shouldWarn: () => isCurrentGuard(guardId) && evaluateDirty(),
  })

  const runGuarded = async (action) => {
    if (!isCurrentGuard(guardId) || !evaluateDirty()) {
      return await action()
    }

    openPrompt({
      guardId,
      proceed: action,
      cancel: null,
    })
    return undefined
  }

  onBeforeRouteLeave((to, from, next) => {
    if (!isCurrentGuard(guardId) || !evaluateDirty()) {
      next()
      return
    }

    store.commit('app/TOGGLE_PAGE_CHANGING', false)
    openPrompt({
      guardId,
      proceed: () => next(),
      cancel: () => next(false),
    })
  })

  onBeforeUnmount(() => {
    if (!isCurrentGuard(guardId)) {
      return
    }

    activeGuard.value = null
    if (pendingAction.value && pendingAction.value.guardId === guardId) {
      clearPendingAction()
      resetDialogState()
    }
  })

  return {
    runGuarded,
  }
}

/**
 * 提供 App 全局 host 使用的未儲存提示狀態與操作方法。
 *
 * host component 應只負責渲染與事件轉發，不需了解目前頁面的 dirty/save/discard 細節。
 *
 * @returns 全局提示對話框所需的狀態與 handler。
 */
export const useUnsavedLeavePromptHost = () => {
  const onConfirm = async () => {
    if (!pendingAction.value || !activeGuard.value) {
      clearPendingAction()
      resetDialogState()
      return
    }

    dialogState.saving = true
    try {
      const isSaved = await activeGuard.value.saveChanges()
      if (!isSaved) {
        return
      }
      await runPendingProceed()
    } catch (err) {
      console.error('[useUnsavedLeavePrompt] Save failed:', err)
    // Optionally: surface error to user or re-throw
    } finally {
      dialogState.saving = false
    }
  }

  const onDiscard = async () => {
    if (!pendingAction.value || !activeGuard.value) {
      clearPendingAction()
      resetDialogState()
      return
    }

    try {
      await activeGuard.value.discardChanges()
    } catch (err) {
      console.error('[useUnsavedLeavePrompt] Discard failed:', err)
    }
    await runPendingProceed()
  }

  const onDismiss = () => {
    runPendingCancel()
  }

  return {
    dialogState: readonly(dialogState),
    onConfirm,
    onDiscard,
    onDismiss,
  }
}
